'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Edit2, Trash2, UserCheck, UserX, Search, Save, Loader2, AlertTriangle, Shield, KeyRound, Crown, Check, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import Modal from '@/components/ui/Modal';

interface PlatformUser {
  id: string;
  email: string;
  full_name: string;
  full_name_ar: string;
  role: string;
  phone: string | null;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

const platformRoleOptions = [
  { value: 'super_admin', label: { ar: 'مدير المنصة', en: 'Platform Admin' }, color: 'bg-amber-50 text-amber-700', icon: Crown },
  { value: 'platform_support', label: { ar: 'دعم المنصة', en: 'Platform Support' }, color: 'bg-blue-50 text-blue-700', icon: Shield },
  { value: 'platform_finance', label: { ar: 'مالية المنصة', en: 'Platform Finance' }, color: 'bg-green-50 text-green-700', icon: Shield },
  { value: 'platform_viewer', label: { ar: 'مراقب المنصة', en: 'Platform Viewer' }, color: 'bg-gray-50 text-gray-700', icon: Eye },
];

const platformRolePermissions: Record<string, string[]> = {
  super_admin: ['*'],
  platform_support: ['companies_view', 'join_requests_manage', 'complaints_manage', 'contact_messages_manage', 'companies_suspend'],
  platform_finance: ['revenue_view', 'subscriptions_manage', 'packages_manage', 'analytics_view'],
  platform_viewer: ['overview_view', 'companies_view', 'analytics_view'],
};

const permissionLabels: Record<string, { ar: string; en: string }> = {
  '*': { ar: 'صلاحيات كاملة', en: 'Full Access' },
  companies_view: { ar: 'عرض الشركات', en: 'View Companies' },
  join_requests_manage: { ar: 'إدارة طلبات الانضمام', en: 'Manage Join Requests' },
  complaints_manage: { ar: 'إدارة الشكاوى', en: 'Manage Complaints' },
  contact_messages_manage: { ar: 'إدارة رسائل التواصل', en: 'Manage Contact Messages' },
  companies_suspend: { ar: 'تعليق الشركات', en: 'Suspend Companies' },
  revenue_view: { ar: 'عرض الإيرادات', en: 'View Revenue' },
  subscriptions_manage: { ar: 'إدارة الاشتراكات', en: 'Manage Subscriptions' },
  packages_manage: { ar: 'إدارة الباقات', en: 'Manage Packages' },
  analytics_view: { ar: 'عرض التحليلات', en: 'View Analytics' },
  overview_view: { ar: 'عرض النظرة العامة', en: 'View Overview' },
};

interface Props {
  lang: 'ar' | 'en';
}

export default function PlatformUsersPanel({ lang }: Props) {
  const supabase = createClient();
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<PlatformUser | null>(null);
  const [selectedUser, setSelectedUser] = useState<PlatformUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetLoading, setResetLoading] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', role: 'platform_support', phone: '', password: '' });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch users with no company_id (platform-level users) or super_admin role
      const { data } = await supabase
        .from('user_profiles')
        .select('*')
        .or('role.eq.super_admin,role.eq.platform_support,role.eq.platform_finance,role.eq.platform_viewer')
        .order('created_at', { ascending: false });
      if (data) setUsers(data as PlatformUser[]);
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في تحميل المستخدمين' : 'Error loading users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleInviteUser = async () => {
    if (!form.full_name || !form.email || !form.role) {
      toast.error(lang === 'ar' ? 'يرجى تعبئة جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      // Create auth user if password provided
      if (form.password && form.password.length >= 8) {
        const { data: authData, error: authError } = await supabase.auth.admin
          ? await (supabase.auth as any).admin.createUser({
              email: form.email.trim().toLowerCase(),
              password: form.password,
              email_confirm: true,
              user_metadata: { full_name: form.full_name, role: form.role },
            })
          : { data: null, error: new Error('Admin API not available') };

        if (!authError && authData?.user) {
          await supabase.from('user_profiles').upsert([{
            id: authData.user.id,
            email: form.email.trim().toLowerCase(),
            full_name: form.full_name,
            full_name_ar: form.full_name,
            role: form.role,
            phone: form.phone || null,
            company_id: null,
            is_active: true,
          }]);
        }
      } else {
        // Insert profile row — user will register via invite
        const { error } = await supabase.from('user_profiles').insert([{
          email: form.email.trim().toLowerCase(),
          full_name: form.full_name,
          full_name_ar: form.full_name,
          role: form.role,
          phone: form.phone || null,
          company_id: null,
          is_active: false,
        }]);
        if (error && !error.message.includes('duplicate')) throw error;
      }

      toast.success(lang === 'ar' ? `تم إضافة ${form.full_name} كمستخدم للمنصة` : `${form.full_name} added as platform user`);
      setInviteOpen(false);
      setForm({ full_name: '', email: '', role: 'platform_support', phone: '', password: '' });
      fetchUsers();
    } catch (err: any) {
      if (err?.message?.includes('duplicate') || err?.message?.includes('already exists')) {
        toast.error(lang === 'ar' ? 'هذا البريد الإلكتروني مسجل بالفعل' : 'Email already registered');
      } else {
        toast.error(lang === 'ar' ? 'خطأ في إضافة المستخدم' : 'Error adding user');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('user_profiles').update({
        full_name: form.full_name,
        full_name_ar: form.full_name,
        role: form.role,
        phone: form.phone || null,
        updated_at: new Date().toISOString(),
      }).eq('id', selectedUser.id);
      if (error) throw error;
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, ...form, full_name_ar: form.full_name } : u));
      setEditOpen(false);
      toast.success(lang === 'ar' ? 'تم تحديث بيانات المستخدم' : 'User updated successfully');
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في تحديث البيانات' : 'Error updating user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (user: PlatformUser) => {
    if (user.role === 'super_admin') {
      toast.error(lang === 'ar' ? 'لا يمكن تعطيل مدير المنصة الرئيسي' : 'Cannot deactivate main platform admin');
      return;
    }
    try {
      const { error } = await supabase.from('user_profiles').update({ is_active: !user.is_active }).eq('id', user.id);
      if (error) throw error;
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: !u.is_active } : u));
      toast.success(user.is_active
        ? (lang === 'ar' ? 'تم تعطيل المستخدم' : 'User deactivated')
        : (lang === 'ar' ? 'تم تفعيل المستخدم' : 'User activated'));
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في تحديث الحالة' : 'Error updating status');
    }
  };

  const handleResetPassword = async (user: PlatformUser) => {
    if (!user.email) return;
    setResetLoading(user.id);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?next=/reset-password`,
      });
      if (error) throw error;
      toast.success(lang === 'ar' ? `تم إرسال رابط إعادة التعيين إلى ${user.email}` : `Reset link sent to ${user.email}`);
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في إرسال رابط إعادة التعيين' : 'Error sending reset link');
    } finally {
      setResetLoading(null);
    }
  };

  const handleDeleteUser = async (user: PlatformUser) => {
    if (user.role === 'super_admin') {
      toast.error(lang === 'ar' ? 'لا يمكن حذف مدير المنصة الرئيسي' : 'Cannot delete main platform admin');
      return;
    }
    try {
      const { error } = await supabase.from('user_profiles').update({ is_active: false }).eq('id', user.id);
      if (error) throw error;
      setUsers(prev => prev.filter(u => u.id !== user.id));
      setDeleteConfirm(null);
      toast.success(lang === 'ar' ? 'تم إزالة المستخدم' : 'User removed');
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في حذف المستخدم' : 'Error deleting user');
    }
  };

  const getRoleOption = (role: string) => platformRoleOptions.find(r => r.value === role);

  const filtered = users.filter(u =>
    !search || u.full_name?.includes(search) || u.full_name_ar?.includes(search) || u.email?.includes(search)
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[#0D1117]">
            {lang === 'ar' ? 'مستخدمو المنصة والمشرفون' : 'Platform Users & Admins'}
          </h2>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {lang === 'ar' ?'إدارة مشرفي المنصة وأدوارهم وصلاحياتهم — مستقل تماماً عن مستخدمي الشركات' :'Manage platform admins, roles and permissions — completely separate from company users'}
          </p>
        </div>
        <button
          onClick={() => { setForm({ full_name: '', email: '', role: 'platform_support', phone: '', password: '' }); setInviteOpen(true); }}
          className="flex items-center gap-2 h-10 px-4 bg-[#0F5C5C] text-white rounded-xl text-sm font-semibold hover:bg-[#0D4F4F] transition-colors shadow-sm"
        >
          <Plus size={15} />
          {lang === 'ar' ? 'إضافة مشرف' : 'Add Admin'}
        </button>
      </div>

      {/* Privacy Notice */}
      <div className="flex items-start gap-3 p-4 mb-5 rounded-xl border border-amber-200 bg-amber-50">
        <Shield size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-amber-800">
            {lang === 'ar' ? 'منطقة خاصة بإدارة المنصة' : 'Platform Administration Private Zone'}
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            {lang === 'ar' ?'هذه البيانات خاصة بمشرفي المنصة فقط ولا تظهر لأي شركة مسجلة أو مستخدميها بأي شكل من الأشكال.' :'This data is exclusive to platform admins and is never visible to any registered company or their users.'}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-sm">
        <Search size={14} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#6B7280]" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={lang === 'ar' ? 'بحث بالاسم أو البريد...' : 'Search by name or email...'}
          className="w-full h-10 bg-white border border-[#E2E6EA] rounded-xl pe-9 ps-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
        />
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-[#0F5C5C]" />
        </div>
      ) : (
        <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="bg-[#F7F8FA] border-b border-[#E2E6EA]">
                  {[
                    lang === 'ar' ? 'المستخدم' : 'User',
                    lang === 'ar' ? 'الدور' : 'Role',
                    lang === 'ar' ? 'الصلاحيات' : 'Permissions',
                    lang === 'ar' ? 'الحالة' : 'Status',
                    lang === 'ar' ? 'إجراءات' : 'Actions',
                  ].map(h => (
                    <th key={h} className="text-end text-xs font-semibold text-[#6B7280] px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(user => {
                  const roleOpt = getRoleOption(user.role);
                  const perms = platformRolePermissions[user.role] || [];
                  return (
                    <tr key={user.id} className="border-t border-[#F0F2F5] hover:bg-[#F7F8FA] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                            style={{ background: 'linear-gradient(135deg, #0F5C5C 0%, #1A8A8A 100%)' }}>
                            {(user.full_name_ar || user.full_name || '?').charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-[#0D1117] text-sm">{user.full_name_ar || user.full_name}</p>
                            <p className="text-xs text-[#6B7280]" dir="ltr">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${roleOpt?.color || 'bg-gray-50 text-gray-700'}`}>
                          {roleOpt ? (lang === 'ar' ? roleOpt.label.ar : roleOpt.label.en) : user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {perms.includes('*') ? (
                            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                              {lang === 'ar' ? 'صلاحيات كاملة' : 'Full Access'}
                            </span>
                          ) : perms.slice(0, 2).map(p => (
                            <span key={p} className="text-xs bg-[#F0F2F5] text-[#6B7280] px-2 py-0.5 rounded-full">
                              {lang === 'ar' ? permissionLabels[p]?.ar : permissionLabels[p]?.en}
                            </span>
                          ))}
                          {!perms.includes('*') && perms.length > 2 && (
                            <span className="text-xs bg-[#F0F2F5] text-[#6B7280] px-2 py-0.5 rounded-full">
                              +{perms.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                          user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                          {user.is_active ? (lang === 'ar' ? 'نشط' : 'Active') : (lang === 'ar' ? 'معطل' : 'Inactive')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => handleToggleStatus(user)}
                            disabled={user.role === 'super_admin'}
                            className={`p-1.5 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                              user.is_active
                                ? 'hover:bg-red-50 text-[#6B7280] hover:text-red-600'
                                : 'hover:bg-green-50 text-[#6B7280] hover:text-green-600'
                            }`}
                            title={user.is_active ? (lang === 'ar' ? 'تعطيل' : 'Deactivate') : (lang === 'ar' ? 'تفعيل' : 'Activate')}
                          >
                            {user.is_active ? <UserX size={14} /> : <UserCheck size={14} />}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setForm({ full_name: user.full_name_ar || user.full_name, email: user.email, role: user.role, phone: user.phone || '', password: '' });
                              setEditOpen(true);
                            }}
                            className="p-1.5 rounded-md hover:bg-[#0F5C5C]/10 text-[#6B7280] hover:text-[#0F5C5C] transition-colors"
                            title={lang === 'ar' ? 'تعديل' : 'Edit'}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleResetPassword(user)}
                            disabled={resetLoading === user.id}
                            className="p-1.5 rounded-md hover:bg-amber-50 text-[#6B7280] hover:text-amber-600 transition-colors disabled:opacity-50"
                            title={lang === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
                          >
                            {resetLoading === user.id ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(user)}
                            disabled={user.role === 'super_admin'}
                            className="p-1.5 rounded-md hover:bg-red-50 text-[#6B7280] hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title={lang === 'ar' ? 'إزالة' : 'Remove'}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-sm text-[#6B7280]">
                      {lang === 'ar' ? 'لا توجد نتائج' : 'No results found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Role Permissions Reference */}
      <div className="mt-6 bg-white border border-[#E2E6EA] rounded-2xl shadow-sm p-5">
        <h3 className="font-bold text-[#0D1117] text-sm mb-4">
          {lang === 'ar' ? 'مرجع أدوار وصلاحيات المنصة' : 'Platform Roles & Permissions Reference'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {platformRoleOptions.map(role => (
            <div key={role.value} className="border border-[#E2E6EA] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${role.color}`}>
                  {lang === 'ar' ? role.label.ar : role.label.en}
                </span>
              </div>
              <div className="space-y-1.5">
                {(platformRolePermissions[role.value] || []).map(perm => (
                  <div key={perm} className="flex items-center gap-2">
                    <Check size={11} className="text-green-600 shrink-0" />
                    <span className="text-xs text-[#374151]">
                      {lang === 'ar' ? permissionLabels[perm]?.ar : permissionLabels[perm]?.en}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Admin Modal */}
      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)}
        title={lang === 'ar' ? 'إضافة مشرف للمنصة' : 'Add Platform Admin'}
        subtitle={lang === 'ar' ? 'سيتم منحه صلاحيات إدارة المنصة وفق الدور المحدد' : 'Will be granted platform management permissions per selected role'}
        size="md" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
              {lang === 'ar' ? 'الاسم الكامل' : 'Full Name'} <span className="text-red-500">*</span>
            </label>
            <input type="text" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              className="w-full h-10 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
              placeholder={lang === 'ar' ? 'محمد بن عبدالله' : 'John Smith'} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
              {lang === 'ar' ? 'البريد الإلكتروني' : 'Email'} <span className="text-red-500">*</span>
            </label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full h-10 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
              placeholder="admin@nexora.sa" dir="ltr" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
                {lang === 'ar' ? 'الدور' : 'Role'} <span className="text-red-500">*</span>
              </label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full h-10 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20">
                {platformRoleOptions.map(r => (
                  <option key={r.value} value={r.value}>{lang === 'ar' ? r.label.ar : r.label.en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
                {lang === 'ar' ? 'رقم الجوال' : 'Phone'}
              </label>
              <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full h-10 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
                placeholder="05XXXXXXXX" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
              {lang === 'ar' ? 'كلمة المرور (اختياري)' : 'Password (optional)'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full h-10 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg px-3 pe-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
                placeholder={lang === 'ar' ? 'اتركها فارغة لإرسال رابط التسجيل' : 'Leave empty to send registration link'}
                dir="ltr"
              />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                className="absolute top-1/2 -translate-y-1/2 end-3 text-[#6B7280] hover:text-[#0D1117]">
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-xs text-[#6B7280] mt-1">
              {lang === 'ar' ? 'إذا تركتها فارغة، سيتم إنشاء حساب معلق ويمكنك إرسال رابط إعادة تعيين لاحقاً' : 'If left empty, a pending account is created and you can send a reset link later'}
            </p>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setInviteOpen(false)}
              className="flex-1 h-10 bg-[#F7F8FA] text-[#374151] rounded-lg text-sm font-semibold hover:bg-[#F0F2F5] transition-colors border border-[#E2E6EA]">
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button onClick={handleInviteUser} disabled={saving}
              className="flex-1 h-10 bg-[#0F5C5C] text-white rounded-lg text-sm font-semibold hover:bg-[#0D4F4F] transition-colors disabled:opacity-70 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {saving ? (lang === 'ar' ? 'جاري الإضافة...' : 'Adding...') : (lang === 'ar' ? 'إضافة المشرف' : 'Add Admin')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)}
        title={lang === 'ar' ? 'تعديل بيانات المشرف' : 'Edit Admin Details'}
        subtitle={selectedUser?.full_name_ar || selectedUser?.full_name}
        size="md" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
              {lang === 'ar' ? 'الاسم الكامل' : 'Full Name'}
            </label>
            <input type="text" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              className="w-full h-10 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
                {lang === 'ar' ? 'الدور' : 'Role'}
              </label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                disabled={selectedUser?.role === 'super_admin'}
                className="w-full h-10 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 disabled:opacity-60">
                {platformRoleOptions.map(r => (
                  <option key={r.value} value={r.value}>{lang === 'ar' ? r.label.ar : r.label.en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
                {lang === 'ar' ? 'رقم الجوال' : 'Phone'}
              </label>
              <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full h-10 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setEditOpen(false)}
              className="flex-1 h-10 bg-[#F7F8FA] text-[#374151] rounded-lg text-sm font-semibold hover:bg-[#F0F2F5] transition-colors border border-[#E2E6EA]">
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button onClick={handleUpdateUser} disabled={saving}
              className="flex-1 h-10 bg-[#0F5C5C] text-white rounded-lg text-sm font-semibold hover:bg-[#0D4F4F] transition-colors disabled:opacity-70 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? (lang === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (lang === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}
        title={lang === 'ar' ? 'تأكيد الإزالة' : 'Confirm Removal'}
        size="sm" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        {deleteConfirm && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle size={18} className="text-red-600 shrink-0" />
              <p className="text-sm text-red-700">
                {lang === 'ar'
                  ? `هل أنت متأكد من إزالة المشرف "${deleteConfirm.full_name_ar || deleteConfirm.full_name}"؟`
                  : `Are you sure you want to remove admin "${deleteConfirm.full_name_ar || deleteConfirm.full_name}"?`}
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 h-10 bg-[#F7F8FA] text-[#374151] rounded-lg text-sm font-semibold hover:bg-[#F0F2F5] transition-colors border border-[#E2E6EA]">
                {lang === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button onClick={() => handleDeleteUser(deleteConfirm)}
                className="flex-1 h-10 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors">
                {lang === 'ar' ? 'تأكيد الإزالة' : 'Confirm Removal'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
